import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";

import { Header, Loading, Pulldown } from "../../components";
import {
  PHARMACY_INFORMATION,
  PRESCRIPTION_TYPE_SELECTOR,
} from "../../routes/path";
import {
  getPharmacy,
  getCurrentPosition,
  getPharmacyInformation,
} from "../../server/axios";
import {
  setPharmacySelected,
  setApplyPrescriptionInfo,
} from "../../store/actions/prescription";
import store from "../../store";
import { getLocationWithPromise } from "../../utils/getLocationWithPromise";
import { closeKeyboard } from "../../utils/closeKeyboard";

/**
 * 薬局選択
 * @returns
 */
const PharmacySelector = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingOpen, setLoadingOpen] = useState(true);
  const [endY, setEndY] = useState(0);
  const [pharmacy, setPharmacy] = useState("1");
  const [prefecture, setPrefecture] = useState("");
  const [pharmacyName, setPharmacyName] = useState("");
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [radius, setRadius] = useState(1000);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [prefectureArray, setPrefectureArray] = useState([]);
  const firstGetPharmacyCompletedRef = useRef(false);
  const firstSetPhamacyRef = useRef(false);
  const [prefectureFlag, setPrefectureFlag] = useState(false);
  const [radiusFlag, setRadiusFlag] = useState(false);
  const [conditionSearchFlag, setConditionSearchFlag] = useState(false);
  //エラーメッセージ表示
  const [openSnackbar, setOpenSnackbar] = useState({
    status: false,
    message: "",
  });

  const handleSnackbarClose = () => {
    setOpenSnackbar({
      status: false,
      message: "",
    });
  };

  const customerSelectedByState = (state) =>
    state.prescription.customerSelected;
  let customerSelected = customerSelectedByState(store.getState());

  const pharmacySelectedByState = (state) =>
    state.prescription.pharmacySelected;
  let pharmacySelected = pharmacySelectedByState(store.getState());

  const selectLineInfo = (state) => state.prescription.lineInfo;
  const lineInfo = selectLineInfo(store.getState());

  const applyPrescriptionEnterTypeByState = (state) =>
    state.prescription.applyPrescriptionEnterType;
  let applyPrescriptionEnterType = applyPrescriptionEnterTypeByState(
    store.getState()
  );

  const handlePrefectureChange = (value) => {
    setPrefecture(value);
  };

  const handleClick = (index) => {
    let selectedRows = rows[index];
    store.dispatch(
      setPharmacySelected({
        selectedRows,
      })
    );

    const locationState = location.state ?? {};
    navigate(PHARMACY_INFORMATION, {
      state: {
        ...locationState,
        pharmacy: pharmacy,
        radius: radius,
        pharmacyName: pharmacyName,
        prefecture: prefecture,
      },
    });
  };

  const getPrefectureArray = () => {
    getPharmacy({ mode: "4", liffId: lineInfo.liffId }).then((res) => {
      if (res.data.prefecture) {
        setPrefectureArray(res.data.prefecture);
      }
      setLoadingOpen(false);
    });
  };

  useEffect(() => {
    if (
      applyPrescriptionEnterType === 1 &&
      pharmacySelected &&
      pharmacySelected.selectedRows
    ) {
      setLoadingOpen(true);
      getPharmacyInformation({
        tenantId: pharmacySelected.selectedRows.tenantId,
        locationId: pharmacySelected.selectedRows.locationId,
        prescription: pharmacySelected.selectedRows.prescription,
        lineId: lineInfo.lineId,
      }).then((res) => {
        const applyPrescriptionInfo = {
          tenantId: pharmacySelected.selectedRows.tenantId,
          locationId: pharmacySelected.selectedRows.locationId,
        };
        store.dispatch(setApplyPrescriptionInfo(applyPrescriptionInfo));

        setLoadingOpen(false);
        navigate(PRESCRIPTION_TYPE_SELECTOR, {
          state: {
            prescription: res.data.target.prescription,
            week: res.data.target.businessWeekInfoList,
          },
        });
      });
    } else {
      setLoadingOpen(true);
      getPrefectureArray();
    }
  }, []);

  // 検索条件絞込
  useEffect(() => {
    (async () => {
      if (applyPrescriptionEnterType !== 1) {
        setLoadingOpen(true);
        // デフォルト
        if (!prefecture) {
          const locationPrefecture = getCurrentPosition();
          setPrefecture(locationPrefecture);
        }

        let _latitude = latitude;
        let _longitude = longitude;
        if (pharmacy === "2") {
          try {
            const position = await getLocationWithPromise();
            _latitude = position.coords.latitude;
            _longitude = position.coords.longitude;

            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);
          } catch (error) {
            setOpenSnackbar({
              status: true,
              message: error.message,
            });
          }
        }

        setPharmacyName("");
        await getPharmacy({
          mode: Number(pharmacy),
          customerId: customerSelected.selectedRows.customerId,
          familyId: customerSelected.selectedRows.familyId,
          latitude: _latitude,
          longitude: _longitude,
          radius: radius,
          pharmacyName: pharmacyName,
          prefecture: prefecture,
          lineId: lineInfo.lineId,
        }).then((res) => {
          setRows(res.data.target);
          setLoadingOpen(false);
          firstGetPharmacyCompletedRef.current = true;

          // 薬局情報から戻ってきた場合に選択していた状態を反映する。
          // 条件から検索
          if (prefectureFlag) {
            const { state } = location;
            conditionSearch(state.pharmacyName, res.data.target);
            handlePrefectureChange(state.prefecture);
            setPrefectureFlag(false);
            if (prefecture === state.prefecture) {
              setConditionSearchFlag(false);
            } else {
              setConditionSearchFlag(true);
            }
          }

          // 現在地から検索
          if (radiusFlag) {
            const { state } = location;
            setRadius(state.radius);
            setRadiusFlag(false);
          }
        });
      }
    })();
  }, [pharmacy]);

  useEffect(() => {
    if (firstGetPharmacyCompletedRef.current) {
      if (!firstSetPhamacyRef.current) {
        const { state } = location;
        const _pharmacy = state?.pharmacy;

        // 薬局情報から戻ってきた場合に選択していた状態を反映する。
        // pharmacyを変更して検索条件絞込のuseEffectを実行する。
        if (typeof _pharmacy !== "undefined") {
          switch (_pharmacy) {
            case "2": {
              setPharmacy(state.pharmacy);
              setRadiusFlag(true);
              break;
            }

            case "3": {
              setPharmacy(state.pharmacy);
              setPrefectureFlag(true);
              break;
            }

            default: {
              break;
            }
          }
        }

        // 初回のレンダリング時、過去に利用した薬局がない場合はデフォルトの値を「条件から検索」に設定する。
        if (typeof _pharmacy === "undefined" && rows.length === 0) {
          const selectedPharmacyNumber = "3";
          setPharmacy(selectedPharmacyNumber);
        }
      }
      firstSetPhamacyRef.current = true;
    }
  }, [rows]);

  // 都道府県変化時に薬局を再検索
  useEffect(() => {
    if (pharmacy === "3") {
      setLoadingOpen(true);
      setPharmacyName("");
      getPharmacy({
        mode: Number(pharmacy),
        prefecture: prefecture,
        lineId: lineInfo.lineId,
      }).then((res) => {
        setRows(res.data.target);

        // 薬局情報から戻ってきた場合に選択していた状態を反映する。
        if (conditionSearchFlag) {
          const { state } = location;
          conditionSearch(state.pharmacyName, res.data.target);
          setConditionSearchFlag(false);
        }

        setLoadingOpen(false);
      });
    }
  }, [prefecture]);

  //距離半径変化時に薬局を再検索、近くの薬局の切り替え再検索
  useEffect(() => {
    if (pharmacy === "2") {
      setLoadingOpen(true);
      setPharmacyName("");
      getPharmacy({
        mode: Number(pharmacy),
        latitude: latitude,
        longitude: longitude,
        radius: radius,
        lineId: lineInfo.lineId,
      }).then((res) => {
        setRows(res.data.target);
        setLoadingOpen(false);
      });
    }
  }, [radius]);

  function conditionSearch(pharmacyInput, pharmacyRows = []) {
    setPharmacyName(pharmacyInput);

    const selectedRharmacyRows =
      pharmacyRows.length === 0 ? rows : pharmacyRows;

    const filtered = selectedRharmacyRows.filter(
      (location) => location.locationName.indexOf(pharmacyInput) !== -1
    );
    setFilteredRows(filtered);
  }

  return (
    <>
      <Loading open={loadingOpen} />
      <div
        onTouchStartCapture={(e) => {
          setEndY(0);
        }}
        onTouchMoveCapture={(e) => {
          setEndY(e.touches[0].clientY);
        }}
        onTouchEndCapture={(e) => {
          if (endY !== 0) {
            e.stopPropagation();
          }
        }}
      >
        {applyPrescriptionEnterType !== 1 && (
          <>
            <Header title="処方箋申込[薬局選択]" />
            <Snackbar
              open={openSnackbar.status}
              autoHideDuration={7000}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              onClose={handleSnackbarClose}
              sx={{ mt: "8vh" }}
            >
              <Alert
                severity="error"
                sx={{ width: "100%" }}
                style={{ whiteSpace: "pre-wrap" }}
                onClose={handleSnackbarClose}
              >
                {openSnackbar.message}
              </Alert>
            </Snackbar>
            <Box
              height="83vh"
              marginTop="9vh"
              width="95%"
              marginLeft="auto"
              marginRight="auto"
            >
              <Stack>
                <Typography variant="subtitle1" align="left">
                  ご利用する薬局を選択してください。
                </Typography>
                <Box width="85%" marginLeft="auto" marginRight="auto">
                  <FormControl>
                    <RadioGroup
                      aria-labelledby="demo-radio-buttons-group-label"
                      value={pharmacy}
                      onChange={(event) => {
                        setPharmacy(event.target.value);
                      }}
                      name="radio-buttons-group"
                    >
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label="過去にご利用した薬局"
                      />
                      <FormControlLabel
                        value="2"
                        control={<Radio />}
                        label="現在地から検索"
                      />
                      <Stack
                        direction="row"
                        alignItems="center"
                        marginLeft="10%"
                      >
                        <Box width="30%">
                          <Typography>半径</Typography>
                        </Box>
                        <Box width="70%">
                          <FormControl fullWidth>
                            <Pulldown
                              size="small"
                              withLabel={false}
                              withMenuProps={true}
                              disabled={pharmacy !== "2"}
                              items={[
                                {
                                  id: "1000",
                                  value: "1km",
                                },
                                {
                                  id: "2000",
                                  value: "2km",
                                },
                              ]}
                              value={radius}
                              setValue={setRadius}
                            />
                          </FormControl>
                        </Box>
                      </Stack>
                      <FormControlLabel
                        value="3"
                        control={<Radio />}
                        label="条件から検索"
                      />
                      <Stack
                        direction="row"
                        alignItems="center"
                        marginLeft="10%"
                      >
                        <Box width="30%">
                          <Typography>薬局名</Typography>
                        </Box>
                        <Box width="70%">
                          <FormControl fullWidth>
                            <TextField
                              width="70%"
                              variant="outlined"
                              size="small"
                              disabled={pharmacy !== "3"}
                              value={pharmacyName}
                              onChange={(e) => {
                                conditionSearch(e.target.value);
                              }}
                              onKeyDown={(event) => closeKeyboard(event)}
                            />
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        marginLeft="10%"
                      >
                        <Box width="30%">
                          <Typography>都道府県</Typography>
                        </Box>
                        <Box width="70%">
                          <FormControl fullWidth>
                            <Pulldown
                              size="small"
                              withLabel={false}
                              withMenuProps={true}
                              disabled={pharmacy !== "3"}
                              items={prefectureArray.map((item) => ({
                                id: item,
                                value: item,
                                label: item,
                              }))}
                              value={prefecture}
                              setValue={handlePrefectureChange}
                            />
                          </FormControl>
                        </Box>
                      </Stack>
                    </RadioGroup>
                  </FormControl>
                </Box>

                <TableContainer
                  sx={{
                    overflowY: "scroll",
                    height: "40vh",
                    border: "1px solid #ccc",
                    width: "80%",
                    marginLeft: "auto",
                    marginRight: "auto",
                    marginTop: "2vh",
                    marginBottom: "15px",
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{ width: "40%" }} align="center">
                          <Typography>薬局名</Typography>
                        </TableCell>
                        <TableCell style={{ width: "60%" }} align="center">
                          <Typography>本日</Typography>
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pharmacyName
                        ? filteredRows.map((row, index) => (
                            <TableRow
                              key={index}
                              onClick={() => handleClick(index, row)}
                            >
                              <TableCell align="center">
                                {row.locationName}
                              </TableCell>
                              <TableCell align="center">
                                {row.startTime.substring(0, 5)}~
                                {row.endTime.substring(0, 5)}
                              </TableCell>
                            </TableRow>
                          ))
                        : rows.map((row, index) => (
                            <TableRow
                              key={index}
                              onClick={() => handleClick(index, row)}
                            >
                              <TableCell align="center">
                                {row.locationName}
                              </TableCell>
                              <TableCell align="center">
                                {row.startTime.substring(0, 5)}~
                                {row.endTime.substring(0, 5)}
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Box>
          </>
        )}
      </div>
    </>
  );
};

export default PharmacySelector;
